import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useAddToCart } from "@/hooks/useCart";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { AddCartItemRequest } from "@/types/api";

export const PENDING_PURCHASE_KEY = "hd_pending_purchase";

interface PendingPurchase {
  body: AddCartItemRequest;
  goCheckout: boolean;
  savedAt?: number;
}

// Persisted for 24h so the selection survives sign-up + email-verification
// (which can happen in a different tab), then is discarded.
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * After an anonymous user logs in to finish a purchase, this page replays the
 * selection they made while logged out (stashed in sessionStorage by the
 * purchase flow), adds it to their cart, and forwards them to checkout. If
 * there's nothing pending it just sends them to plans.
 */
export function ResumePurchasePage() {
  const navigate = useNavigate();
  const addToCart = useAddToCart();
  const { toast } = useToast();
  const ran = React.useRef(false);

  React.useEffect(() => {
    if (ran.current) return; // guard against StrictMode double-invoke
    ran.current = true;

    const raw = localStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) {
      navigate("/plans", { replace: true });
      return;
    }
    localStorage.removeItem(PENDING_PURCHASE_KEY);

    let pending: PendingPurchase;
    try {
      pending = JSON.parse(raw) as PendingPurchase;
    } catch {
      navigate("/plans", { replace: true });
      return;
    }

    // Ignore stale selections (older than the TTL).
    if (pending.savedAt && Date.now() - pending.savedAt > PENDING_TTL_MS) {
      navigate("/plans", { replace: true });
      return;
    }

    addToCart
      .mutateAsync(pending.body)
      .then(() => navigate(pending.goCheckout ? "/checkout" : "/cart", { replace: true }))
      .catch((err) => {
        toast({
          title: "Couldn't finish adding to cart",
          description: err instanceof ApiError ? err.message : "Please try selecting your plan again.",
          variant: "destructive"
        });
        navigate("/plans", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-background text-muted-foreground">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
      <p className="text-sm">Finishing your order…</p>
    </div>
  );
}
